import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies, categories } from '@/mocks/movies';
import useSEO from '@/hooks/useSEO';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'dianying');
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'year'>('latest');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeCategory]);

  const currentCat = categories.find((c) => c.slug === activeCategory);
  const catName = currentCat?.name || '电影';

  let filteredMovies = movies.filter((m) => m.category === activeCategory);

  if (sortBy === 'rating') {
    filteredMovies = [...filteredMovies].sort((a, b) => b.rating - a.rating);
  } else if (sortBy === 'year') {
    filteredMovies = [...filteredMovies].sort((a, b) => b.year - a.year);
  }

  const subcategories = [...new Set(filteredMovies.map((m) => m.subcategory))];

  useSEO({
    title: `${catName} - 人人影院免费高清在线观看`,
    description: `人人影院提供最新${catName}免费高清在线观看，海量${catName}资源每日更新，无需缓冲流畅播放，追剧看片一站解决。`,
    keywords: `${catName},免费${catName},高清${catName},${catName}在线观看,人人影院`,
    canonicalUrl: `https://renrencinema.com/category/${activeCategory}`,
    ogTitle: `${catName} - 人人影院免费高清在线观看`,
    ogDescription: `人人影院提供最新${catName}免费高清在线观看，海量${catName}资源每日更新，无需缓冲流畅播放。`,
    ogUrl: `https://renrencinema.com/category/${activeCategory}`,
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": `${catName} - 人人影院`,
        "description": `人人影院提供最新${catName}免费高清在线观看`,
        "url": `https://renrencinema.com/category/${activeCategory}`,
        "isPartOf": { "@type": "WebSite", "name": "人人影院", "url": "https://renrencinema.com/" }
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://renrencinema.com/" },
          { "@type": "ListItem", "position": 2, "name": catName, "item": `https://renrencinema.com/category/${activeCategory}` }
        ]
      }
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: catName },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading">
                  {catName}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {filteredMovies.length} 部作品
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                <div className="flex bg-background-50 rounded-lg p-1">
                  {([
                    { key: 'latest', label: '最新' },
                    { key: 'rating', label: '评分' },
                    { key: 'year', label: '年份' },
                  ] as const).map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap cursor-pointer transition-all duration-200 ${
                        sortBy === opt.key
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                  }`}
                >
                  <span className="w-3.5 h-3.5 inline-flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>

            {subcategories.length > 0 && (
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                {subcategories.map((sub) => (
                  <span
                    key={sub}
                    className="px-3 py-1 text-xs text-foreground-600 bg-background-50 border border-background-200 rounded-full whitespace-nowrap"
                  >
                    {sub}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}